// FILE: QueuedComposerTurnRow.tsx
// Purpose: One row of the composer queue popover — preview, attachments, auto-send switch and the
//   actions that send, edit or drop the message.
// Layer: Chat composer presentation
// Depends on: the composer queue contract and caller-owned mutation callbacks.

import { memo } from "react";
import type { ComposerQueuedMessage } from "@taskin-code/contracts";

import { formatAttachmentSize } from "@taskin-code/shared/attachmentKinds";
import { resolveAttachmentPreviewUrl } from "~/lib/attachmentUrls";
import { EllipsisIcon, PaperclipIcon } from "~/lib/icons";
import { cn } from "~/lib/utils";
import { Menu, MenuItem, MenuPopup, MenuTrigger } from "../ui/menu";
import { Switch } from "../ui/switch";

function formatQueuedAt(createdAt: string): string {
  const parsed = new Date(createdAt);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

export const QueuedComposerTurnRow = memo(function QueuedComposerTurnRow(props: {
  item: ComposerQueuedMessage;
  /** True while a mutation for this row is in flight, so its controls stop responding. */
  pending: boolean;
  /** Whether the thread has a turn running right now. Decides what "Send" can mean. */
  hasLiveTurn: boolean;
  onSetAutoSend: (queuedId: string, autoSend: boolean) => void;
  onSend: (queuedId: string) => void;
  onSteer: (queuedId: string) => void;
  onEdit: (item: ComposerQueuedMessage) => void;
  onRemove: (queuedId: string) => void;
}) {
  const { item, pending, hasLiveTurn } = props;
  const queuedAt = formatQueuedAt(item.createdAt);

  // With a turn running and auto-send already on, the server will send this by itself. Offering a
  // live "Send" would promise something new when all it would do is set a flag that is already set.
  const alreadyScheduled = hasLiveTurn && item.autoSend;
  const sendLabel = alreadyScheduled ? "Queued to send" : "Send";

  return (
    <div
      data-testid="queued-follow-up-row"
      className="flex flex-col gap-1.5 rounded-lg px-2 py-2 transition-colors hover:bg-muted/40"
    >
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="line-clamp-2 whitespace-pre-wrap break-words text-[12px] leading-snug text-foreground/85">
            {item.payload.previewText}
          </p>
          {queuedAt ? (
            <span className="mt-0.5 block text-[10px] text-muted-foreground/70">{queuedAt}</span>
          ) : null}
        </div>
        <Menu>
          <MenuTrigger
            render={
              <button
                type="button"
                className="inline-flex size-6 shrink-0 items-center justify-center rounded-lg text-muted-foreground/70 transition-colors hover:bg-muted/60 hover:text-foreground"
                aria-label="Queued follow-up actions"
              />
            }
          >
            <EllipsisIcon className="size-3" />
          </MenuTrigger>
          <MenuPopup align="end" side="top">
            <MenuItem onClick={() => props.onEdit(item)}>Edit queued prompt</MenuItem>
            <MenuItem onClick={() => props.onRemove(item.queuedId)}>Delete queued prompt</MenuItem>
            {hasLiveTurn ? (
              <MenuItem onClick={() => props.onSteer(item.queuedId)}>Send now (steer)</MenuItem>
            ) : null}
          </MenuPopup>
        </Menu>
      </div>

      {item.attachments.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {item.attachments.map((attachment) =>
            // A file has no thumbnail to show, and the route refuses to serve
            // it inline anyway — an `<img>` here would render as broken.
            attachment.type === "file" ? (
              <span
                key={attachment.id}
                title={`${attachment.name} · ${formatAttachmentSize(attachment.sizeBytes)}`}
                className="inline-flex max-w-[160px] items-center gap-1 rounded border border-border/60 px-1.5 py-0.5 text-[11px] text-muted-foreground"
              >
                <PaperclipIcon className="size-3 shrink-0" />
                <span className="min-w-0 truncate">{attachment.name}</span>
              </span>
            ) : (
              <img
                key={attachment.id}
                src={resolveAttachmentPreviewUrl(attachment.id)}
                alt={attachment.name}
                title={attachment.name}
                className="size-8 rounded border border-border/60 object-cover"
              />
            ),
          )}
        </div>
      ) : null}

      {item.lastError !== null ? (
        <p className="text-[11px] leading-snug text-destructive">{item.lastError}</p>
      ) : null}

      <div className="flex items-center justify-between gap-2">
        <label className="flex cursor-pointer items-center gap-1.5 text-[11px] text-muted-foreground">
          <Switch
            checked={item.autoSend}
            disabled={pending}
            onCheckedChange={(checked) => props.onSetAutoSend(item.queuedId, checked)}
          />
          <span>Auto-send</span>
        </label>
        <button
          type="button"
          className={cn(
            "inline-flex items-center rounded-lg bg-muted/80 px-2 py-0.5 text-[11px] font-medium text-foreground transition-colors hover:bg-muted",
            "disabled:pointer-events-none disabled:opacity-50",
          )}
          disabled={pending || alreadyScheduled}
          title={
            alreadyScheduled
              ? "Auto-send is on: the server will send this when the current turn ends."
              : hasLiveTurn
                ? "Turns on auto-send so this goes out when the current turn ends."
                : "Send this message now."
          }
          onClick={() => props.onSend(item.queuedId)}
        >
          {sendLabel}
        </button>
      </div>
    </div>
  );
});
